// t2a.utils.js
// Pure fee math for Blitz T2A (Transmitter Atomic).
// No React. No side-effects. Import anywhere — frontend or copy into the fee service.

export const PAYMENT_RAILS = {
  paypal: { label: "PayPal",      pct: 0.029, fixed: 0.30 },
  stripe: { label: "Card / Bank", pct: 0.035, fixed: 0.30 },
};

/**
 * Compute T2A amounts for a single transfer.
 *
 * @param {number} agreedAmount  - What the two parties agreed on (the "headline" number)
 * @param {"paypal"|"stripe"} rail
 * @param {"sender"|"recipient"} feePayer
 * @returns {{
 *   senderPays: number,        // what leaves the sender's pocket / payment method
 *   recipientReceives: number, // what lands in recipient's balance
 *   fee: number,               // processing fee
 * }}
 */
export function computeT2A(agreedAmount, rail, feePayer) {
  const amt = parseFloat(agreedAmount) || 0;
  if (amt <= 0) return { senderPays: 0, recipientReceives: 0, fee: 0 };

  const r = PAYMENT_RAILS[rail] || PAYMENT_RAILS.paypal;

  if (feePayer === "sender") {
    // Sender gross-up: they pay enough so recipient lands on agreedAmount exactly.
    // gross = (agreed + fixed) / (1 - pct)
    const gross = (amt + r.fixed) / (1 - r.pct);
    const fee   = gross - amt;
    return {
      senderPays:        +gross.toFixed(2),
      recipientReceives: +amt.toFixed(2),
      fee:               +fee.toFixed(2),
    };
  }

  // feePayer === "recipient": sender pays agreedAmount, fee deducted from recipient
  const fee               = amt * r.pct + r.fixed;
  const recipientReceives = Math.max(0, amt - fee);
  return {
    senderPays:        +amt.toFixed(2),
    recipientReceives: +recipientReceives.toFixed(2),
    fee:               +fee.toFixed(2),
  };
}

/** Format a number as USD string, e.g. 1234.5 → "$1,234.50" */
export const fmtUSD = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);

/** True if a string looks like a valid email */
export const isValidEmail = (v) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim().toLowerCase());

/** True if a string looks like a phone number */
export const isValidPhone = (v) =>
  /^\+?[0-9][0-9()\-\s]{6,}$/.test(String(v || "").trim());

/**
 * Given a raw string (username / email / phone), return its recipient kind.
 * @returns {"internal"|"email"|"phone"}
 */
export function inferRecipientKind(q) {
  const s = (q || "").trim();
  if (isValidEmail(s)) return "email";
  if (isValidPhone(s)) return "phone";
  return "internal";
}

/**
 * Build the transfer item payload for /api/gu-balance/batch-transfer.
 * Attaches T2A metadata so the backend can audit fee decisions.
 */
export function buildTransferItem(recipient, senderPays, agreedAmount, fee, feePayer, rail) {
  const base = {
    amount: senderPays.toString(),
    metadata: {
      t2a: true,
      agreed_amount:  agreedAmount,
      fee_amount:     fee,
      fee_payer:      feePayer,
      payment_rail:   rail,
    },
  };
  if (recipient.kind === "internal") return { ...base, type: "internal", recipient_global_user_id: recipient.globalUserId };
  if (recipient.kind === "email")    return { ...base, type: "email",    recipient_email:          recipient.email };
  return                                    { ...base, type: "phone",    phone_number:             recipient.phoneNumber };
}